"use client";

import { useRef, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { TicketSheet } from "@/components/tools/printables/exit-ticket-page";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  cutLines,
  layoutTicket,
  parsePrompts,
  type RatingStyle,
  TICKET_COUNTS,
  type TicketsPerPage,
  ticketCells,
} from "@/lib/tool-kit/printables/exit-ticket";
import {
  type Orientation,
  type PaperSize,
  pageGeometry,
} from "@/lib/tool-kit/printables/page";

/** English strings. A translated content file overrides these through `ui`. */
export const EXIT_TICKET_GENERATOR_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  templateLabel: "Start from a template",
  templates: {
    "321": {
      label: "3-2-1",
      prompts:
        "3 things I learned | 3\n2 things I found interesting | 2\n1 question I still have | 1",
      rating: "none",
    },
    muddiest: {
      label: "Muddiest point",
      prompts: "What was the muddiest point in today's lesson? | 4",
      rating: "none",
    },
    understanding: {
      label: "Check for understanding",
      prompts:
        "One thing I learned today | 2\nOne thing I still need help with | 2",
      rating: "scale",
    },
    summary: {
      label: "One-sentence summary",
      prompts: "Summarise today's lesson in one sentence. | 3",
      rating: "none",
    },
    custom: { label: "Custom", prompts: "", rating: "none" },
  },
  defaultTitle: "Exit Ticket",
  titleLabel: "Ticket title",
  promptsLabel: "Prompts — one per line",
  promptsHint:
    "Add a bar and a number to set the answer lines for a prompt, for example: One question I still have | 2. Up to six prompts.",
  ratingLabel: "Rating row",
  ratings: {
    none: "None",
    scale: "Scale from 1 to 5",
    traffic: "Three choices (need help, nearly, got it)",
  },
  ratingPromptLabel: "Rating question",
  ratingPromptDefault: "How well did I understand today's lesson?",
  ratingLow: "Not yet",
  ratingHigh: "Got it",
  traffic: ["Need help", "Nearly there", "Got it"],
  perPageLabel: "Tickets per page",
  perPage: {
    "2": "2 tickets (1 × 2)",
    "3": "3 tickets (1 × 3)",
    "4": "4 tickets (2 × 2)",
    "6": "6 tickets (2 × 3)",
    "8": "8 tickets (2 × 4)",
  },
  nameDate: "Name and date lines",
  nameLabel: "Name",
  dateLabel: "Date",
  topic: "Topic line",
  topicLabel: "Topic",
  smallTypeNotice:
    "The prompts are long for tickets this small, so the type is reduced. Use fewer tickets per page or shorten the prompts for easier reading.",
  previewLabel: "Exit tickets preview",
} as const;

type TemplateKey = keyof typeof EXIT_TICKET_GENERATOR_STRINGS.templates;

export default function ExitTicketGeneratorWidget() {
  const strings = useToolUi(EXIT_TICKET_GENERATOR_STRINGS);
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [template, setTemplate] = useState<TemplateKey>("321");
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [promptsText, setPromptsText] = useState<string>(
    strings.templates["321"].prompts
  );
  const [rating, setRating] = useState<RatingStyle>("none");
  const [ratingPrompt, setRatingPrompt] = useState<string>(
    strings.ratingPromptDefault
  );
  const [perPage, setPerPage] = useState<TicketsPerPage>(4);
  const [nameDate, setNameDate] = useState(true);
  const [topic, setTopic] = useState(false);
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [orientation, setOrientation] = useState<Orientation>("portrait");

  function chooseTemplate(next: TemplateKey) {
    setTemplate(next);
    if (next !== "custom") {
      const chosen = strings.templates[next];
      setPromptsText(chosen.prompts);
      setRating(chosen.rating as RatingStyle);
    }
  }

  const geometry = pageGeometry(paper, orientation);
  const cells = ticketCells(geometry, perPage);
  const first = cells[0];
  const layout = layoutTicket(
    { x: 0, y: 0, width: first?.width ?? 90, height: first?.height ?? 120 },
    measure,
    {
      title,
      topicLabel: strings.topicLabel,
      nameLabel: strings.nameLabel,
      dateLabel: strings.dateLabel,
      showNameDate: nameDate,
      showTopic: topic,
      prompts: parsePrompts(promptsText),
      rating,
      ratingPrompt,
      ratingLow: strings.ratingLow,
      ratingHigh: strings.ratingHigh,
      ratingTraffic: [
        strings.traffic[0],
        strings.traffic[1],
        strings.traffic[2],
      ],
    }
  );
  const pages = [
    <TicketSheet
      cells={cells}
      cuts={cutLines(geometry, perPage)}
      key="tickets"
      layout={layout}
      traffic={rating === "traffic"}
    />,
  ];

  return (
    <div className="space-y-6 print:m-0 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="et-template"
            label={strings.templateLabel}
            onChange={chooseTemplate}
            options={(Object.keys(strings.templates) as TemplateKey[]).map(
              (value) => ({ value, label: strings.templates[value].label })
            )}
            value={template}
          />
          <TextField
            id="et-title"
            label={strings.titleLabel}
            onChange={setTitle}
            value={title}
          />
        </div>
        <TextAreaField
          hint={strings.promptsHint}
          id="et-prompts"
          label={strings.promptsLabel}
          onChange={(value) => {
            setPromptsText(value);
            setTemplate("custom");
          }}
          prose
          rows={5}
          value={promptsText}
        />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <SelectField
            id="et-rating"
            label={strings.ratingLabel}
            onChange={setRating}
            options={(["none", "scale", "traffic"] as const).map((value) => ({
              value,
              label: strings.ratings[value],
            }))}
            value={rating}
          />
          {rating === "none" ? null : (
            <TextField
              id="et-rating-prompt"
              label={strings.ratingPromptLabel}
              onChange={setRatingPrompt}
              value={ratingPrompt}
            />
          )}
          <SelectField
            id="et-per-page"
            label={strings.perPageLabel}
            onChange={(value) => setPerPage(Number(value) as TicketsPerPage)}
            options={TICKET_COUNTS.map((value) => ({
              value: String(value),
              label:
                strings.perPage[String(value) as keyof typeof strings.perPage],
            }))}
            value={String(perPage)}
          />
          <PaperControls
            idPrefix="et"
            onOrientation={setOrientation}
            onPaper={setPaper}
            orientation={orientation}
            paper={paper}
            strings={strings}
          />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={nameDate}
            label={strings.nameDate}
            onChange={setNameDate}
          />
          <Checkbox checked={topic} label={strings.topic} onChange={setTopic} />
        </div>
        {layout.scale < 0.65 ? (
          <Notice>{strings.smallTypeNotice}</Notice>
        ) : null}
        <PrintBar
          fileName={`exit-tickets-${paper}`}
          pageTotal={1}
          paper={paper}
          strings={strings}
          svgRef={svgRef}
        />
      </div>
      <PrintablePages
        className={orientation === "landscape" ? "max-w-3xl" : "max-w-xl"}
        geometry={geometry}
        label={strings.previewLabel}
        pages={pages}
        svgRef={svgRef}
      />
    </div>
  );
}
